import type { RateInput, RateOutput } from '@allocab/allocab-extranet-api-types';
import { useQuery } from '@tanstack/react-query';

import type { DashboardRateInput, DashboardRateResponse } from '../../../types/extranet-api.types';
import { getExtranetApi } from '../extranetApi';
import { toRateInput } from '../mappers';
import { extranetQueryKeys } from '../queryKeys';

type RateMetric =
  | 'coverage-rate'
  | 'punctuality-rate'
  | 'incident-rate'
  | 'security-incident-rate'
  | 'service-rate'
  | 'late-customer-cancellation-rate'
  | 'no-show-passenger-rate';

const fetchRate = async (metric: RateMetric, input: DashboardRateInput): Promise<DashboardRateResponse> => {
  // Back livre `rate` en ratio 0..1 (ex. 0.92 = 92%) — on multiplie par 100 pour
  // matcher la convention front (KpiCard / formatPercent attendent un %).
  const data = await getExtranetApi().get<RateInput, RateOutput>(`/v1/performance/${metric}`, toRateInput(input));
  return { value: data.rate * 100 };
};

const useRate = (metric: RateMetric, input: DashboardRateInput) =>
  useQuery({
    queryKey: extranetQueryKeys.dashboard.rate(metric, input),
    queryFn: () => fetchRate(metric, input),
    enabled: Boolean(input.dateRange?.from && input.dateRange?.to),
  });

export const useCoverageRate = (input: DashboardRateInput) => useRate('coverage-rate', input);
export const usePunctualityRate = (input: DashboardRateInput) => useRate('punctuality-rate', input);
export const useIncidentRate = (input: DashboardRateInput) => useRate('incident-rate', input);
export const useSecurityIncidentRate = (input: DashboardRateInput) => useRate('security-incident-rate', input);
export const useServiceRate = (input: DashboardRateInput) => useRate('service-rate', input);
export const useLateCancellationRate = (input: DashboardRateInput) => useRate('late-customer-cancellation-rate', input);
export const useNoShowPassengerRate = (input: DashboardRateInput) => useRate('no-show-passenger-rate', input);

/** Alias façade : "Taux d'acceptation" = même valeur que Taux de couverture (cf. spec §5.D RG-KPI-1) */
export const useAcceptanceRate = useCoverageRate;
